import React, { useState } from "react";
import { Upload, Download, CheckCircle2, AlertTriangle, Send, FileCheck2 } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useCities } from "@/context/CitiesContext";
import { toast } from "sonner";

const inputCls = "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

export default function BulkSubmit({ showSubmitter = false, onDone }) {
  const { cities } = useCities();
  const [file, setFile] = useState(null);
  const [city, setCity] = useState("worcester");
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const check = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setChecking(true); setPreview(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("city", city);
    try {
      const { data } = await api.post("/bulk/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.valid.length === 0) toast.error("No valid rows found — check the errors below");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const submit = async () => {
    if (!preview?.valid?.length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bulk/submit", {
        events: preview.valid, submitter_name: name, submitter_email: email,
      });
      const msg = data.published
        ? `${data.inserted} imported — ${data.published} published, ${data.pending} pending review`
        : `${data.inserted} events submitted for review`;
      toast.success(msg);
      setFile(null); setPreview(null); setName(""); setEmail("");
      onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="bulk-submit">
      <div className="bg-ec-surface border border-white/10 p-6">
        <h3 className="font-heading text-2xl tracking-wide">Bulk Import via CSV</h3>
        <p className="text-sm text-zinc-400 mt-2">
          1. Download the template &nbsp;2. Fill in your events &nbsp;3. Upload &amp; check &nbsp;4. Submit for review.
          Columns: <code className="text-ec-volt">title, sport, team, venue, start_at, end_at, description, ticket_url, city, organiser_email</code>. Dates ISO, e.g. 2026-07-12T15:00.
          <br /><span className="text-zinc-500">Optional <code className="text-ec-volt">organiser_email</code>: matches a provider's login to attribute events to their account (admin uploads publish these immediately; others stay pending).</span>
        </p>
        <a href={`${API}/events/template`} download data-testid="bulk-template-btn" className="inline-flex items-center gap-2 overline border border-white/15 px-4 py-2.5 mt-4 hover:border-ec-volt hover:text-ec-volt transition-colors">
          <Download size={15} /> Download CSV Template
        </a>

        {showSubmitter && (
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Your Name</span>
              <input data-testid="bulk-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="overline text-zinc-500 mb-2 block">Your Email</span>
              <input data-testid="bulk-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></label>
          </div>
        )}

        <div className="mt-5">
          <span className="overline text-zinc-500 mb-2 block">Default city (used when a row has no city)</span>
          <select data-testid="bulk-city" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls}>
            {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <input data-testid="bulk-file" type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="flex-1 text-sm text-zinc-300 file:mr-3 file:overline file:border-0 file:bg-white/10 file:text-white file:px-4 file:py-2.5" />
          <button data-testid="bulk-check-btn" onClick={check} disabled={checking} className="inline-flex items-center gap-2 overline border border-ec-volt text-ec-volt px-6 py-2.5 hover:bg-ec-volt hover:text-black transition-colors disabled:opacity-50">
            <FileCheck2 size={15} /> {checking ? "Checking…" : "Check File"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-4 bg-black/40 border border-white/10 p-5" data-testid="bulk-preview-result">
          <div className="flex flex-wrap gap-5 mb-4">
            <span className="inline-flex items-center gap-2 overline text-ec-volt"><CheckCircle2 size={16} /> {preview.valid.length} ready</span>
            {preview.errors.length > 0 && <span className="inline-flex items-center gap-2 overline text-ec-red"><AlertTriangle size={16} /> {preview.errors.length} with errors</span>}
          </div>

          {preview.valid.length > 0 && (
            <div className="border border-white/10 overflow-x-auto mb-4">
              <table className="w-full text-sm" data-testid="bulk-preview-table">
                <thead className="bg-black/50"><tr className="text-left overline text-zinc-500"><th className="p-2.5">Title</th><th className="p-2.5">Category</th><th className="p-2.5">When</th><th className="p-2.5">City</th><th className="p-2.5">Provider</th></tr></thead>
                <tbody>
                  {preview.valid.slice(0, 50).map((e, i) => (
                    <tr key={i} className={i % 2 ? "bg-white/[0.03]" : ""}>
                      <td className="p-2.5 font-semibold">{e.title}</td>
                      <td className="p-2.5 text-zinc-400">{e.sport}</td>
                      <td className="p-2.5 text-zinc-400 whitespace-nowrap">{new Date(e.start_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="p-2.5 text-zinc-400 capitalize">{e.city}</td>
                      <td className="p-2.5 whitespace-nowrap" data-testid={`bulk-row-provider-${i}`}>
                        {e.organiser_matched
                          ? <span className="text-ec-volt">{e.organiser_name}</span>
                          : e.organiser_email
                            ? <span className="text-ec-red" title="Email not found — will import unattributed">not found</span>
                            : <span className="text-zinc-600">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.errors.length > 0 && (
            <ul className="text-sm text-ec-red list-disc pl-5 mb-4 space-y-1" data-testid="bulk-preview-errors">
              {preview.errors.map((er, i) => <li key={i}>Row {er.row} ({er.title}): {er.errors.join(", ")}</li>)}
            </ul>
          )}

          <button data-testid="bulk-submit-btn" onClick={submit} disabled={submitting || preview.valid.length === 0} className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-7 py-3.5 hover:-translate-y-0.5 transition-all disabled:opacity-40">
            <Send size={16} /> {submitting ? "Submitting…" : `Submit ${preview.valid.length} events for review`}
          </button>
        </div>
      )}
    </div>
  );
}
